import { inject, Injectable, Type } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { SaveCampaignModelComponent } from '../components/pages/assets/configuration/campaigns/models/save-campaign-model.component';
import { CesDialogFooterComponent } from '../components/ui/dialog-footer/dialog-footer.component';
import { DialogFooterService } from './dialog-footer.service';
import { ModalConfig, ModalHelperService } from './modal-helper.service';

interface CampaignModelDialogFooterConfig {
  cancelLabel?: string;
  submitLabel?: string;
  submitDisabled?: () => boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
}

interface CampaignModelDialogData {
  mode: 'create';
  isNested: true;
  dialogId: string;
  campaignId?: number;
  assetId?: number;
}

export interface CampaignModelSavePayload {
  name: string;
  description?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignModelDialogService {
  private readonly dialogService = inject(DialogService);
  private readonly footerService = inject(DialogFooterService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly translate = inject(TranslocoService);

  private openDialog<T>(
    component: Type<T>,
    dialogId: string,
    header: string,
    footerConfig: CampaignModelDialogFooterConfig,
    data: Partial<Omit<CampaignModelDialogData, 'mode' | 'isNested' | 'dialogId'>> = {},
    sizeConfig: ModalConfig = this.modalHelper.getFormConfig(),
  ): DynamicDialogRef<T> {
    this.footerService.setFooterConfig(dialogId, {
      cancelLabel: footerConfig.cancelLabel ?? this.translate.translate('common.buttons.cancel'),
      submitLabel: footerConfig.submitLabel ?? this.translate.translate('common.buttons.validate'),
      cancelDisabled: false,
      submitDisabled: footerConfig.submitDisabled ?? (() => false),
      onCancel: footerConfig.onCancel ?? (() => {}),
      onSubmit: footerConfig.onSubmit ?? (() => {}),
    });

    const ref = this.dialogService.open(component, {
      ...sizeConfig,
      header,
      closable: true,
      closeOnEscape: true,
      templates: {
        footer: CesDialogFooterComponent,
      },
      data: {
        mode: 'create',
        isNested: true,
        dialogId,
        ...data,
      } satisfies CampaignModelDialogData,
    });

    this.footerService.setCurrentDialogId(dialogId);

    return ref;
  }

  /**
   * Opens a dialog to save current configuration as a model
   */
  openSaveModelDialog(campaignId: number, assetId: number): Observable<CampaignModelSavePayload | undefined> {
    const dialogId = `dialog_save_model_${Date.now()}`;
    let componentInstance: SaveCampaignModelComponent | null = null;

    return new Observable(observer => {
      const ref = this.openDialog<SaveCampaignModelComponent>(
        SaveCampaignModelComponent,
        dialogId,
        this.translate.translate('entities.campaign.models.saveTitle'),
        {
          cancelLabel: this.translate.translate('common.buttons.cancel'),
          submitLabel: this.translate.translate('common.buttons.save'),
          submitDisabled: () => {
            const isValid = componentInstance?.isValid();
            return !isValid;
          },
          onCancel: () => {
            ref?.close();
            observer.next(undefined);
            observer.complete();
          },
          onSubmit: () => {
            if (componentInstance?.isValid()) {
              const result: CampaignModelSavePayload = {
                name: componentInstance.modelName().trim(),
                description: componentInstance.modelDescription().trim(),
              };
              ref?.close(result);
              observer.next(result);
              observer.complete();
            }
          },
        },
        { campaignId, assetId },
      );

      ref.onChildComponentLoaded.subscribe({
        next: instance => {
          componentInstance = instance;
        },
        error: err => {
          console.error('Error in onChildComponentLoaded:', err);
        },
      });

      ref.onClose.subscribe({
        next: (result: CampaignModelSavePayload | undefined) => {
          if (!observer.closed) {
            observer.next(result);
            observer.complete();
          }
        },
        error: err => {
          console.error('Error in onClose:', err);
          observer.error(err);
        },
      });
    });
  }
}
